import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { faCoins } from "@fortawesome/pro-regular-svg-icons";

import { Information } from "@/components/Information/Information";

import { Analytics } from "../schema/analytics";
import { KpiSection } from "../components/KpiSection";
import { formatNumber } from "../utils/percentage";

export interface CreditsSectionProps {
	data: Analytics;
}

interface CreditTile {
	labelKey: string;
	value: number | null;
}

/** "—" rather than 0: no reply yet is missing data, not a free reply. */
const formatRatio = (value: number | null): string => (value === null ? "—" : formatNumber(value));

export const CreditsSection: FunctionComponent<CreditsSectionProps> = ({ data }) => {
	const { t } = useTranslation();

	// The API returns the campaign-attributed sum only when a campaign is selected; that
	// is also when the ratios switch to it, so the two always describe the same spend.
	const isCampaignScoped = data.campaignAttributedCredits !== null;
	const spend = data.campaignAttributedCredits ?? data.totalCredits ?? 0;

	const tiles: CreditTile[] = [
		{ labelKey: "analytics.sections.credits.used", value: spend },
		{ labelKey: "analytics.sections.credits.perQualified", value: data.creditsPerQualifiedLead },
		{ labelKey: "analytics.sections.credits.perReply", value: data.creditsPerReply },
		{ labelKey: "analytics.sections.credits.perPositive", value: data.creditsPerPositiveReply },
	];

	return (
		<KpiSection
			headerAccessory={
				isCampaignScoped ? (
					<Information information={t("analytics.sections.credits.campaignScopeHint")} />
				) : undefined
			}
			icon={faCoins}
			title={t("analytics.sections.credits.title")}
		>
			<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
				{tiles.map((tile) => (
					<div
						key={tile.labelKey}
						className="flex flex-col rounded-xl border border-border-light bg-brand-white px-4 py-3"
					>
						<div className="text-xs text-text-meta uppercase tracking-wide">{t(tile.labelKey)}</div>
						<div className="text-2xl font-semibold text-text tabular-nums mt-1">
							{formatRatio(tile.value)}
						</div>
					</div>
				))}
			</div>
		</KpiSection>
	);
};
